import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { DEFAULT_LANG, canonicalLang, type Lang } from '@shared/config';
import { LIVE_LOCALES, liveLocale, type Market } from '@/marketing/i18n/locales';
import { useSwitchFunnelLocale } from '../model/useSwitchFunnelLocale';

/**
 * Flag + short label, mirroring the marketing `SiteNav` switcher.
 *
 * The flag follows the MARKET, not the language: inside Belgium every language is
 * Belgian (NL there is Flemish, not Dutch-from-NL), so NL/FR/EN all fly 🇧🇪. Only
 * the international market falls back to the language-level flag.
 *
 * NOTE: `SiteNav` keeps its own copy of this map because `src/marketing/**` is
 * vendored 1:1 from done-it-design and is not hand-edited here. Fold the two
 * together when the funnel header is rebuilt on the marketing nav shell.
 */
const LOCALE_FLAG: Record<string, string> = {
  en: '🇬🇧', nl: '🇳🇱', 'fr-be': '🇧🇪', fr: '🇫🇷', es: '🇪🇸', de: '🇩🇪',
  it: '🇮🇹', pt: '🇵🇹', pl: '🇵🇱', no: '🇳🇴', ro: '🇷🇴', bg: '🇧🇬', tr: '🇹🇷', lt: '🇱🇹',
};

const flagFor = (code: string) =>
  liveLocale(code)?.market === 'belgium' ? '🇧🇪' : (LOCALE_FLAG[code] ?? '🌐');

const shortLabel = (code: string) => code.split('-')[0].toUpperCase();

/**
 * Header language selector — DS-styled dropdown over the LIVE locales (DIT-399).
 *
 * It used to list the funnel's private 16-code registry, several of whose codes
 * are not routable at the edge — picking "Nederlands (BE)" or "Français" walked
 * the user straight into a 404. The list is now derived from `LIVE_LOCALES` and
 * scoped to the active market, so every entry resolves.
 */
export default function LanguageSelector() {
  const { i18n } = useTranslation();
  const switchLocale = useSwitchFunnelLocale();
  const params = useParams<{ lang?: string }>();
  const [open, setOpen] = useState(false);

  const current = useMemo<Lang>(
    () => canonicalLang(params.lang) ?? canonicalLang(i18n.resolvedLanguage) ?? DEFAULT_LANG,
    [params.lang, i18n.resolvedLanguage],
  );

  // Only offer languages within the active market — switching market is a
  // different decision (and a different price list), not a language change.
  const market: Market = liveLocale(current)?.market ?? 'international';
  const options = useMemo(() => LIVE_LOCALES.filter((l) => l.market === market), [market]);

  const onPick = (next: Lang) => {
    setOpen(false);
    if (next === current) return;
    void i18n.changeLanguage(next);
    switchLocale(next);
  };

  // One live language in this market → nothing to switch; render nothing rather
  // than a dead chip (same rule as the marketing switcher).
  if (options.length < 2) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 font-ds text-[14px] font-medium text-ink-soft transition-colors hover:text-ink"
      >
        <span className="text-[15px] leading-none">{flagFor(current)}</span>
        {shortLabel(current)}
        <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-2 max-h-80 w-56 overflow-y-auto rounded-2xl border border-line bg-white p-1 shadow-[0_12px_40px_rgba(15,23,42,0.12)]"
          >
            <div className="grid grid-cols-2 gap-0.5">
              {options.map((l) => (
                <button
                  key={l.code}
                  role="menuitemradio"
                  aria-checked={l.code === current}
                  onClick={() => onPick(l.code)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-left font-ds text-[14px] transition-colors hover:bg-bg-subtle ${
                    l.code === current ? 'font-semibold text-primary' : 'font-medium text-ink-soft hover:text-ink'
                  }`}
                >
                  <span className="text-[15px] leading-none">{flagFor(l.code)}</span>
                  {shortLabel(l.code)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
